import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getPromotionPackage, PROMOTION_PACKAGES } from "./promotions/packages";
import { buildAuctionSlug } from "./auction-slug";

/** Public — list packages for UI (server-authoritative). */
export const listPromotionPackages = createServerFn({ method: "GET" }).handler(async () => {
  return PROMOTION_PACKAGES.map((p) => ({
    days: p.days,
    amount_pence: p.amount_pence,
    label: p.label,
    description: p.description,
  }));
});

/**
 * Create a Stripe Checkout Session for a listing promotion.
 * Server-authoritative pricing: the browser sends only the auction id and
 * package days.
 */
export const createPromotionCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        auctionId: z.string().uuid(),
        packageDays: z.union([z.literal(3), z.literal(7), z.literal(14)]),
        origin: z.string().url(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const pkg = getPromotionPackage(data.packageDays);
    if (!pkg) throw new Error("Invalid package");

    const { data: auction, error: aErr } = await supabase
      .from("auctions")
      .select("id,seller_id,status,end_time,title,images")
      .eq("id", data.auctionId)
      .maybeSingle();
    if (aErr) throw new Error(aErr.message);
    if (!auction) throw new Error("Auction not found");
    if (auction.seller_id !== userId) throw new Error("Not your listing");
    if (auction.status !== "active") {
      throw new Error(`Cannot promote a listing with status "${auction.status}"`);
    }
    if (auction.end_time && new Date(auction.end_time) <= new Date()) {
      throw new Error("Auction has already ended");
    }

    // Idempotency: reuse any pending row for this auction+seller+package that
    // hasn't been paid yet AND hasn't yet been sent to Stripe.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("listing_promotions")
      .select("id,stripe_checkout_session_id,status")
      .eq("auction_id", auction.id)
      .eq("seller_id", userId)
      .eq("package_days", pkg.days)
      .eq("status", "pending")
      .is("stripe_checkout_session_id", null)
      .order("created_at", { ascending: false })
      .maybeSingle();

    let promoId: string;
    if (existing) {
      promoId = existing.id as string;
    } else {
      const { data: created, error: cErr } = await supabaseAdmin
        .from("listing_promotions")
        .insert({
          auction_id: auction.id,
          seller_id: userId,
          package_days: pkg.days,
          amount_pence: pkg.amount_pence,
          status: "pending",
        })
        .select("id")
        .maybeSingle();
      if (cErr || !created) throw new Error(cErr?.message ?? "Could not create promotion");
      promoId = created.id as string;
    }

    // Try to consume a Featured Credit first (Pro/Business benefit).
    const { data: creditRes } = await supabaseAdmin.rpc("consume_featured_credit" as never, {
      _user_id: userId,
      _promotion_id: promoId,
    } as never);
    const creditRow = (Array.isArray(creditRes) ? creditRes[0] : creditRes) as
      | { consumed?: boolean; unlimited?: boolean; remaining?: number }
      | null;
    if (creditRow && (creditRow.consumed || creditRow.unlimited)) {
      // Activate immediately, skip Stripe.
      await supabaseAdmin
        .from("listing_promotions")
        .update({
          status: "active",
          amount_pence: 0,
          paid_at: new Date().toISOString(),
          starts_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + pkg.days * 24 * 3600 * 1000).toISOString(),
        })
        .eq("id", promoId);
      // Refresh auction promoted_until via existing RPC if it exists.
      await supabaseAdmin.rpc("_recompute_auction_promoted_until" as never, {
        _auction_id: auction.id,
      } as never);
      await supabaseAdmin.from("admin_audit_log").insert({
        actor_id: userId,
        action: "listing_promotion.activate_credit",
        auction_id: auction.id,
        metadata: {
          promotion_id: promoId,
          package_days: pkg.days,
          unlimited: !!creditRow.unlimited,
        },
      });
      return { url: null, sessionId: null, promotionId: promoId, used_credit: true, unlimited: !!creditRow.unlimited };
    }

    const { getStripe } = await import("./stripe-checkout.server");
    const stripe = getStripe();
    const { getPromotionPriceId } = await import("./promotions.server");
    const priceId = getPromotionPriceId(pkg.days);

    const image = (auction.images as string[] | null)?.[0];
    const lineItem = priceId
      ? { price: priceId, quantity: 1 }
      : {
          quantity: 1,
          price_data: {
            currency: "gbp",
            unit_amount: pkg.amount_pence,
            product_data: {
              name: `Featured Listing (${pkg.days} days) — ${auction.title ?? "Auction"}`,
              images: image ? [image] : undefined,
              metadata: { auction_id: auction.id },
            },
          },
        };

    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      payment_method_types: ["card"],
      // deno-lint-ignore no-explicit-any
      line_items: [lineItem as any],
      client_reference_id: `promo_${promoId}`,
      metadata: {
        promotion_id: promoId,
        auction_id: auction.id,
        seller_id: userId,
        package_days: String(pkg.days),
        kind: "listing_promotion",
      },
      payment_intent_data: {
        metadata: {
          promotion_id: promoId,
          auction_id: auction.id,
          seller_id: userId,
          package_days: String(pkg.days),
          kind: "listing_promotion",
        },
      },
      success_url: `${data.origin}/my-auctions?promoted=${auction.id}&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${data.origin}/auction/${buildAuctionSlug(auction as never)}`,
    });

    await supabaseAdmin
      .from("listing_promotions")
      .update({ stripe_checkout_session_id: session.id })
      .eq("id", promoId);

    return { url: session.url, sessionId: session.id, promotionId: promoId };
  });

/** Seller — list their own promotions (optionally scoped to an auction). */
export const listMyPromotions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ auctionId: z.string().uuid().optional() }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("listing_promotions")
      .select("id,auction_id,package_days,amount_pence,status,starts_at,expires_at,paid_at,cancelled_at,created_at")
      .eq("seller_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (data.auctionId) q = q.eq("auction_id", data.auctionId);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

/** Admin — list all promotions. */
export const adminListPromotions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        status: z.enum(["all", "pending", "active", "expired", "cancelled", "failed"]).optional(),
        limit: z.number().int().min(1).max(500).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role" as never, {
      _user_id: context.userId,
      _role: "admin",
    } as never);
    const { data: isMod } = await context.supabase.rpc("has_role" as never, {
      _user_id: context.userId,
      _role: "moderator",
    } as never);
    if (!isAdmin && !isMod) throw new Error("Forbidden");

    let q = context.supabase
      .from("listing_promotions")
      .select(
        "id,auction_id,seller_id,package_days,amount_pence,status,starts_at,expires_at,stripe_checkout_session_id,stripe_payment_intent_id,paid_at,cancelled_at,cancelled_by,created_at,auctions(title),profiles:seller_id(display_name,email)",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 200);
    if (data.status && data.status !== "all") q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

/** Admin — cancel a promotion (no automatic refund). */
export const adminCancelPromotion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ promotionId: z.string().uuid(), note: z.string().max(500).optional() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role" as never, {
      _user_id: context.userId,
      _role: "admin",
    } as never);
    if (!isAdmin) throw new Error("Admins only");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: promo } = await supabaseAdmin
      .from("listing_promotions")
      .select("id,auction_id,status")
      .eq("id", data.promotionId)
      .maybeSingle();
    if (!promo) throw new Error("Not found");
    if (promo.status === "cancelled") return { ok: true };

    const { error: uErr } = await supabaseAdmin
      .from("listing_promotions")
      .update({
        status: "cancelled",
        cancelled_at: new Date().toISOString(),
        cancelled_by: context.userId,
      })
      .eq("id", promo.id);
    if (uErr) throw new Error(uErr.message);

    // Recompute the auction's featured window
    await supabaseAdmin.rpc("_recompute_auction_promoted_until" as never, {
      _auction_id: promo.auction_id,
    } as never);

    // Audit log
    await supabaseAdmin.from("admin_audit_log").insert({
      actor_id: context.userId,
      action: "listing_promotion.cancel",
      auction_id: promo.auction_id,
      metadata: {
        promotion_id: promo.id,
        prior_status: promo.status,
        note: data.note ?? null,
      },
    });

    return { ok: true };
  });
